// src/components/charts/MetricScatterChart.jsx
import React, { memo, useMemo } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import LoadingBouncyDots from "../ui/LoadingBouncyDots";

ChartJS.register(LinearScale, PointElement, Tooltip, Legend, Title);

function MetricScatterChart({
  height = 240,
  datasets,
  xLabel,
  yLabel,
  xMin,
  xMax,
  yMin,
  yMax,
  showLegend = false,
  showTooltip = true,
  showGrid = true,
  loading = false,
  loadingText = "",
  overlayClassName = "bg-white/40 backdrop-blur-[1px]",
}) {
  const primary = getPrimaryColour();

  const finalDatasets = useMemo(() => {
    if (!datasets || !datasets.length) return [];
    return datasets.map((ds) => ({
      ...ds,
      pointBackgroundColor:
        ds.pointBackgroundColor ??
        primary.replace(")", ", 0.35)").replace("rgb", "rgba"),
      pointBorderColor: ds.pointBorderColor ?? primary,
      pointBorderWidth: ds.pointBorderWidth ?? 1,
      pointRadius: ds.pointRadius ?? 3,
      pointHoverRadius: ds.pointHoverRadius ?? 5,
    }));
  }, [datasets, primary]);

  const hasData = finalDatasets.some((ds) => (ds.data ?? []).length > 0);

  const data = useMemo(() => ({ datasets: finalDatasets }), [finalDatasets]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: showLegend },
        tooltip: { enabled: showTooltip },
        title: { display: false },
      },
      scales: {
        x: {
          type: "linear",
          min: 0,
          max: typeof xMax === "number" ? xMax : undefined,
          grid: { color: showGrid ? "rgba(0,0,0,0.06)" : "transparent" },
          border: { color: "rgba(0,0,0,0.2)" },
          title: { display: !!xLabel, text: xLabel },
        },
        y: {
          type: "linear",
          min: 0,
          max: typeof yMax === "number" ? yMax : undefined,
          grid: { color: showGrid ? "rgba(0,0,0,0.06)" : "transparent" },
          border: { color: "rgba(0,0,0,0.2)" },
          title: { display: !!yLabel, text: yLabel },
        },
      },

      animation: { duration: 300 },
    }),
    [showLegend, showTooltip, showGrid, xLabel, yLabel, xMin, xMax, yMin, yMax]
  );

  return (
    <div className="relative" style={{ height }}>
      <Scatter data={data} options={options} />
      {(loading || !hasData) && (
        <div
          className={`pointer-events-none absolute inset-0 flex items-center justify-center ${overlayClassName}`}
          aria-hidden="true"
        >
          <LoadingBouncyDots text={loadingText} />
        </div>
      )}
    </div>
  );
}

export default memo(MetricScatterChart);
